import { useEffect, useState } from "react";
import { isReadOnlyRole, type ChecklistTemplateAdminSummary } from "@bitacorapro/api-types";
import { useChecklistTemplatesAdminList, useRecipe } from "../lib/api/queries";
import { ApiError } from "../lib/api/error";
import { canManageChecklistTemplates, isChecklistTemplateAdminEnabled } from "../lib/capabilities";
import { useSession } from "../lib/auth/useSession";
import { ChecklistTemplateAssignDrawer } from "./ChecklistTemplateAssignDrawer";
import { Icon } from "./icons";
import { QueryBoundary } from "./QueryBoundary";
import { SkelTable } from "./skeleton/SkelTable";

// The "checklists por tienda" admin panel (ADR-0079, #0424) — third
// `.panel` on /configuracion, alongside the org-info and
// solicitudes-al-admin panels already there.
//
// Whole-surface gate, not a read-only degrade: GET /checklist-templates
// itself 403s for store_manager/brand_manager (capabilities.ts
// canManageChecklistTemplates doc), so there is no partial render for
// those roles — the panel is absent, same as it is absent for any org
// whose active reportType never sets areaCatalogMode: "org_checklist_template".
export function ChecklistTemplatesPanel() {
  const { data: recipe, isLoading: recipeLoading } = useRecipe();
  const session = useSession();
  const role = session?.organization.role;
  // A read-only role (ADR-0164) never sees the panel: its only control
  // is «Asignar tiendas», a write core-api rejects with ROLE_READ_ONLY.
  const enabled =
    isChecklistTemplateAdminEnabled(recipe) &&
    canManageChecklistTemplates(recipe, role, {
      platformAdmin: session?.user?.platformAdmin === true,
    }) &&
    !isReadOnlyRole(role ?? "");

  const list = useChecklistTemplatesAdminList(enabled);
  const [assigning, setAssigning] = useState<ChecklistTemplateAdminSummary | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Auto-dismiss — the drawer's own onSuccess path already moved focus
  // back to the row trigger, so this toast is a transient confirmation,
  // not something the admin needs to dismiss manually.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Loading (recipe not resolved yet) and definitively-absent both
  // render nothing — isChecklistTemplateAdminEnabled(undefined) already
  // returns false while recipe is loading, so this also guards against
  // a one-frame flash of the panel before the gate settles.
  if (recipeLoading || !enabled) return null;

  // The server's own message when it gave one (an ApiError), the
  // generic title otherwise.
  const errorTitle =
    list.error instanceof ApiError
      ? list.error.message
      : "No pudimos cargar los checklists configurados.";

  return (
    <div className="panel">
      <div className="panel__head">
        <div className="panel__title">Checklists por tienda</div>
      </div>

      {/* A panel of /configuracion, not the page: no announcement of its
          own and an error that never moves focus. */}
      <QueryBoundary
        query={list}
        label="los checklists"
        errorTitle={errorTitle}
        kind="section"
        skeleton={
          <SkelTable
            rows={3}
            cols={4}
            headers={["Checklist", "Alcance", "Tiendas asignadas", ""]}
          />
        }
        isEmpty={(d) => d.templates.length === 0}
        empty={
          <div className="empty">
            <div className="empty__title">Aún no hay checklists configurados.</div>
            <div className="empty__sub">
              Los checklists se cargan desde el equipo BitácoraPro — una vez existan, podrás
              asignarlos a tiendas específicas o dejarlos activos para toda la marca desde aquí.
            </div>
          </div>
        }
      >
        {(data) => (
          <div className="panel__body--flush">
            <table className="dtable">
              <thead>
                <tr>
                  <th>Checklist</th>
                  <th>Alcance</th>
                  <th>Tiendas asignadas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.templates.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <span className="cell-strong">{t.label}</span>
                      {!t.active && <span className="cell-meta"> · Archivado</span>}
                    </td>
                    <td>
                      {t.scope === "brand" ? (
                        <span className="scope-pill scope-pill--brand">
                          <Icon.Tag />
                          Toda la marca
                        </span>
                      ) : (
                        <span className="scope-pill scope-pill--assets">
                          <Icon.Store />
                          Tiendas específicas
                        </span>
                      )}
                    </td>
                    <td>
                      {/* De-emphasized when scope is "brand": the count
                            isn't the operative signal there (a brand-scoped
                            template applies everywhere regardless of it).
                            cell-meta is already the muted tone this table
                            uses for secondary numbers; no --ink-4-specific
                            variant class exists to dim it further without
                            inventing new CSS, so both scopes share it. */}
                      <span className="cell-meta">
                        {t.assignedAssetCount} tienda
                        {t.assignedAssetCount === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="cell-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!t.active}
                        title={!t.active ? "Este checklist está archivado" : undefined}
                        onClick={() => setAssigning(t)}
                      >
                        Asignar tiendas
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      {assigning && (
        <ChecklistTemplateAssignDrawer
          template={assigning}
          onClose={() => setAssigning(null)}
          onSaved={() => setToast("Asignación actualizada")}
        />
      )}

      {toast && (
        <div className="toast-zone">
          <div className="toast toast--ok" role="status" aria-live="polite">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
